import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ExerciseDefinition } from '../../types';
import { apiServices } from '../../api/services';
import { ExercisePrRecord } from '../../api/services/WorkoutHistoryService';
import { BaseApiService } from '../../api/services/BaseApiService';
import { useTranslation } from '../../i18n/provider';
import styles from './ExerciseInfoModal.module.css';

interface ExerciseInfoModalProps {
  exercise: ExerciseDefinition | null;
  onClose: () => void;
  /** TODO.md H4: authenticated-user id, needed to fetch the personal-records section below. Anonymous
   * visitors have no backend-tracked workout history, so the section is skipped entirely when absent
   * rather than shown empty or erroring. */
  userId?: string;
}

const ExerciseInfoModal: React.FC<ExerciseInfoModalProps> = ({ exercise, onClose, userId }) => {
  const { t } = useTranslation();
  // Prevent background scroll while modal is open
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  const [imageError, setImageError] = React.useState(false);
  const [exerciseData, setExerciseData] = React.useState<ExerciseDefinition | null>(null);
  const [prRecord, setPrRecord] = React.useState<ExercisePrRecord | null>(null);

  // Fetch full exercise data from backend when modal opens
  useEffect(() => {
    if (exercise) {
      const fetchExerciseData = async () => {
        try {
          const data = await apiServices.exercises.findExerciseByName(exercise.name);
          setExerciseData(data || null); // Handle undefined case
        } catch (error) {
          console.error('Failed to fetch exercise data:', error);
          setExerciseData(exercise); // Fallback to passed exercise
        }
      };
      fetchExerciseData();
    }
  }, [exercise]);

  // TODO.md H4: personal-records section - authenticated users only, since anonymous visitors have no
  // backend-tracked history for this exercise.
  useEffect(() => {
    if (exercise && userId) {
      apiServices.workoutHistory
        .getExercisePrRecord(userId, exercise.name)
        .then(setPrRecord)
        .catch((error) => {
          console.error('Failed to fetch exercise PR record:', error);
          setPrRecord(null);
        });
    } else {
      setPrRecord(null);
    }
  }, [exercise, userId]);

  const handleImageError = () => {
    setImageError(true);
  };

  if (!exercise) return null;

  // Fall back to imageUrl - the backend stopped returning externalUrl after migration 15 merged it
  // into image_url (TODO.md B3/F4), so exerciseData?.externalUrl is always undefined today. Keep the
  // fallback structure (matching ExerciseInfo.tsx / EditWorkoutExercise.tsx) in case a future field
  // ever repopulates externalUrl, and resolve relative backend paths the same way ExerciseInfo.tsx does.
  const rawImageSrc = exerciseData?.externalUrl || exerciseData?.imageUrl;
  const imageSrc = !imageError && rawImageSrc ? (BaseApiService.resolveImageUrl(rawImageSrc) ?? null) : null;

  return createPortal(
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose} aria-label={t('progress.modal.close')}>✕</button>

        {imageSrc && (
          <div className={styles.imageContainer}>
            <img
              src={imageSrc}
              alt={`${exerciseData?.name || exercise.name} preview`}
              className={styles.exerciseImage}
              loading="lazy"
              onError={handleImageError}
            />
          </div>
        )}

        {!imageSrc && (
          <div className={styles.imageContainer}>
            <div className={styles.imagePlaceholder}>
              <span>🏋️</span>
              <p>{t('progress.modal.imageNotAvailable')}</p>
            </div>
          </div>
        )}

        <div className={styles.exerciseInfo}>
          <h3 className={styles.exerciseName}>{exerciseData?.name || exercise.name}</h3>
          
          {exerciseData?.description && (
            <p className={styles.exerciseDescription}>{exerciseData.description}</p>
          )}
          
          <div className={styles.exerciseMeta}>
            <span className={styles.metaBadge}>
              <strong>{t('progress.modal.muscle')}</strong> {exerciseData?.muscleGroup || exercise.muscleGroup}
            </span>
            {exerciseData?.equipment && (
              <span className={styles.metaBadge}>
                <strong>{t('progress.modal.equipment')}</strong> {exerciseData.equipment}
              </span>
            )}
          </div>

          {prRecord && (
            <div className={styles.personalRecords} data-testid="exercise-pr-section">
              <h4 className={styles.prSectionTitle}>{t('progress.modal.personalRecords.title')}</h4>
              <div className={styles.prCards}>
                {prRecord.exerciseType === 'strength' ? (
                  <>
                    {prRecord.maxWeight && (
                      <div className={styles.prCard} data-testid="pr-max-weight">
                        <span className={styles.prLabel}>{t('progress.modal.personalRecords.maxWeight')}</span>
                        <span className={styles.prValue}>
                          {prRecord.maxWeight.weight} {prRecord.maxWeight.unit}
                        </span>
                        <span className={styles.prSubValue}>
                          {t('progress.modal.personalRecords.repsLabel', { count: prRecord.maxWeight.reps })}
                        </span>
                      </div>
                    )}
                    {prRecord.maxReps && (
                      <div className={styles.prCard} data-testid="pr-max-reps">
                        <span className={styles.prLabel}>{t('progress.modal.personalRecords.maxReps')}</span>
                        <span className={styles.prValue}>
                          {t('progress.modal.personalRecords.repsLabel', { count: prRecord.maxReps.reps })}
                        </span>
                        <span className={styles.prSubValue}>
                          {prRecord.maxReps.weight} {prRecord.maxReps.unit}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  prRecord.maxHoldTime && (
                    <div className={styles.prCard} data-testid="pr-max-hold-time">
                      <span className={styles.prLabel}>{t('progress.modal.personalRecords.maxHoldTime')}</span>
                      <span className={styles.prValue}>
                        {t('progress.modal.personalRecords.secondsLabel', { count: prRecord.maxHoldTime.seconds })}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ExerciseInfoModal;
